import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText,
  MoreVertical,
  Plus,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FilterCard } from "../components/FilterCard";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { useAuditorInvoices } from "../hooks/useAuditorInvoices";
import { InvoiceNatureBadge, invoiceNatureShortLabel } from "../components/InvoiceNatureBadge";
import { pendingAuditStatusFilterOptions } from "../data";
import type { InvoiceReviewStatus } from "../types";

export function PendingInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.pendingAudit;
  const { exportTable, printPage } = usePageActions();
  const { invoices, loading, error, usingApi, hospitalOptions, companyOptions } =
    useAuditorInvoices({ status: "pending" });

  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const [draftHospital, setDraftHospital] = useState("");
  const [draftCompany, setDraftCompany] = useState("");
  const [draftStatus, setDraftStatus] = useState("");

  const [appliedHospital, setAppliedHospital] = useState("");
  const [appliedCompany, setAppliedCompany] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");

  const applyFilters = () => {
    setAppliedHospital(draftHospital);
    setAppliedCompany(draftCompany);
    setAppliedStatus(draftStatus);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setDraftHospital("");
    setDraftCompany("");
    setDraftStatus("");
    setAppliedHospital("");
    setAppliedCompany("");
    setAppliedStatus("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return invoices.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.hospital} ${invoice.company} ${invoice.reference} ${invoice.fileName}`.toLowerCase();

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byHospital = appliedHospital ? invoice.hospital === appliedHospital : true;
      const byCompany = appliedCompany ? invoice.company === appliedCompany : true;
      const byStatus = appliedStatus
        ? invoice.reviewStatus === (appliedStatus as InvoiceReviewStatus)
        : true;

      return byTopSearch && byTableSearch && byHospital && byCompany && byStatus;
    });
  }, [
    invoices,
    topSearch,
    tableSearch,
    appliedHospital,
    appliedCompany,
    appliedStatus,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = filtered.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = filtered.slice(start, start + pageSize);

  const columns = [
    labels.columns.id,
    labels.columns.provider,
    labels.columns.company,
    labels.columns.nature,
    labels.columns.invoice,
    labels.columns.status,
    labels.columns.createdAt,
    labels.columns.actions,
  ];

  const handleExportExcel = () => {
    exportTable(
      "factures-en-attente.csv",
      columns.filter((column) => column !== labels.columns.actions),
      filtered.map((invoice) => [
        String(invoice.displayId),
        invoice.hospital,
        invoice.company,
        invoiceNatureShortLabel(
          invoice.invoiceNature === "forfait_mensuel" ? "forfait_mensuel" : "hors_forfait",
        ),
        invoice.fileName,
        labels.statuses[invoice.reviewStatus],
        invoice.createdAt,
      ]),
    );
  };

  const getReviewBadge = (reviewStatus: InvoiceReviewStatus) => {
    if (reviewStatus === "pendingAudit") {
      return (
        <span className="inline-flex items-center rounded-md bg-primary-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
          {labels.statuses.pendingAudit}
        </span>
      );
    }
    if (reviewStatus === "submitted") {
      return (
        <span className="inline-flex items-center rounded-md bg-teal-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal">
          {labels.statuses.submitted}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-yellow-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-yellow">
        {labels.statuses.inReview}
      </span>
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/auditor"
        items={[
          { label: t.safecheck.nav.invoices },
          { label: labels.title },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
          {loading && <p className="mt-1 text-sm text-muted">Chargement…</p>}
          {error && <p className="mt-1 text-sm text-danger">{error}</p>}
          {usingApi && !loading && (
            <p className="mt-1 text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
          )}
        </div>
        <Link
          to="/dashboard/invoices/submit"
          className="inline-flex items-center gap-2 rounded-lg bg-purple px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple/90"
        >
          <Plus size={16} />
          {labels.submitInvoice}
        </Link>
      </div>

      <FilterCard title={t.safecheck.filters} onFilter={applyFilters} onReset={resetFilters}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField label={labels.filters.hospital}>
            <select
              value={draftHospital}
              onChange={(event) => setDraftHospital(event.target.value)}
              className={selectClass}
            >
              {hospitalOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.filters.allHospitals}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.filters.company}>
            <select
              value={draftCompany}
              onChange={(event) => setDraftCompany(event.target.value)}
              className={selectClass}
            >
              {companyOptions.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.value ? option.label : labels.filters.allCompanies}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.filters.status}>
            <select
              value={draftStatus}
              onChange={(event) => setDraftStatus(event.target.value)}
              className={selectClass}
            >
              {pendingAuditStatusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value ? option.label : labels.filters.allStatuses}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </FilterCard>

      <div className="theme-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full max-w-sm`}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-2 rounded-lg bg-green px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green/90"
            >
              <FileSpreadsheet size={15} />
              {labels.exportExcel}
            </button>
            <button
              type="button"
              onClick={printPage}
              className="inline-flex items-center gap-2 rounded-lg bg-pink px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-pink/90"
            >
              <FileText size={15} />
              {labels.exportPdf}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-card-muted"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>entrées</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    <span className="inline-flex items-center gap-1">
                      {header}
                      {header !== labels.columns.actions && (
                        <span className="flex flex-col">
                          <ChevronUp size={10} className="-mb-1" />
                          <ChevronDown size={10} />
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              ) : (
                pageData.map((invoice, index) => (
                  <tr
                    key={invoice.id}
                    className={`border-b border-border last:border-0 hover:bg-card-muted/30 ${
                      index % 2 === 1 ? "bg-card-muted/20" : "bg-card"
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {invoice.displayId}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.hospital}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.company}</td>
                    <td className="px-4 py-3">
                      <InvoiceNatureBadge
                        nature={invoice.invoiceNature}
                        isForfaitMensuel={invoice.isForfaitMensuel}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.fileName}</td>
                    <td className="px-4 py-3">{getReviewBadge(invoice.reviewStatus)}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.createdAt}</td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/invoices/${invoice.displayId}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-card-muted"
                        title={t.actions.view}
                      >
                        <MoreVertical size={15} />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3 text-sm text-muted">
          <span>
            Affichage de {filtered.length === 0 ? 0 : start + 1} à{" "}
            {Math.min(start + pageSize, filtered.length)} sur {filtered.length} entrées
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-border px-3 py-1.5 disabled:opacity-50"
            >
              Précédent
            </button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-md border border-border px-3 py-1.5 disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
